import type { GroupedUsersList } from '@halo/ui-chat-react';
import { SearchField, GroupedUsers, UserSelectorDialog } from '@halo/ui-chat-react';
import { StateEnum, useAppStore } from '@halo/chat-sdk-react';

interface GroupRemoveMembersDialogProps {
  title: ReactNode;
  checkUserKey: boolean;
  list: GroupedUsersList[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onOK: (selected: any[]) => Promise<any>;
  onClose?: () => void;
}

const GroupCreateCallDialog = ({
  title,
  checkUserKey,
  list,
  isOpen,
  setIsOpen,
  onOK,
}: GroupRemoveMembersDialogProps) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const user = useAppStore((store) => store.user);

  const filteredList = useMemo(() => {
    return list.map(({ items, label }) => {
      const filteredItems = items?.filter((item) => {
        return (
          item.state !== StateEnum.DELETE_ACCOUNT &&
          item?.name?.toLowerCase().includes(searchValue?.toLowerCase()) &&
          item?.user_id !== user?.user_id
        );
      });
      return { label, items: filteredItems };
    });
  }, [list, searchValue, user?.user_id]);

  const {
    listFlat,
    selected,
    canSelectList,
    isSelectedAll,
    toggleSelectUser,
    onSelectAllChange,
    onRemove,
    resetDialogState,
  } = UserSelectorDialog.useUserSelector(filteredList, checkUserKey);

  useLayoutEffect(() => {
    if (!isOpen) {
      resetDialogState();
      setSearchValue('');
    }
  }, [isOpen, resetDialogState]);

  return (
    <UserSelectorDialog.Root isOpen={isOpen} setIsOpen={setIsOpen}>
      <UserSelectorDialog.Header title={title} />
      <UserSelectorDialog.Body
        selectedList={
          <UserSelectorDialog.SelectedList
            selected={selected}
            onRemove={onRemove}
            checkUserKey={checkUserKey}
          />
        }
      >
        <SearchField searchValue={searchValue} onChange={setSearchValue} />
        <GroupedUsers
          list={filteredList}
          toggleSelectUser={toggleSelectUser}
          selected={selected}
          checkUserKey={checkUserKey}
        />
        {listFlat.length > 0 && canSelectList.length > 0 && (
          <UserSelectorDialog.SelectAll isChecked={isSelectedAll} onChange={onSelectAllChange} />
        )}
      </UserSelectorDialog.Body>
      <UserSelectorDialog.Footer
        selected={selected}
        onOK={onOK}
        cancelText={t('buttons.cancel')}
        okText={t('calls.createCall', { count: selected.length })}
      />
    </UserSelectorDialog.Root>
  );
};

const MemoizedGroupCreateCallDialog = memo(GroupCreateCallDialog);

export { MemoizedGroupCreateCallDialog as GroupCreateCallDialog };
